import { Box, IconButton, Skeleton, Typography } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { DeliveryDateLinePriceOverrideDialog } from "./DeliveryDateLinePriceOverrideDialog"

import { PriceUomDisplay } from "@/components/PriceUomDisplay/PriceUomDisplay"
import { type RoundOffResult, RoundOffWarning } from "@/components/RoundOffWarning"
import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import { FormDatePickerWithCustomActions } from "@/components/UiKit/FormControls/FormDatePickerWithCustomActions"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { DATE_FORMAT, UOM_RENDER_PRECISION } from "@/constants/formats"
import type { CustomerPurchaseOrderPricingTotalsLineResult } from "@/graphql/codegen/graphql"
import { SalesOrderStatus } from "@/graphql/codegen/graphql"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import { useRoundOffWarning } from "@/hooks/useRoundOffWarning"
import { SalesOrderStatusChip } from "@/screens/CustomerOrders/components/CustomerOrderListPage/SalesOrderStatusChip"
import { useGetShipFromTransitDays } from "@/screens/CustomerOrders/hooks/useGetShipFromTransitDays"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type {
  CpoDeliveryDateLineFormValues,
  CustomerPurchaseOrderLineItemsFormValues,
} from "@/screens/CustomerOrders/schemas"
import type { LinkedQuantityField, ProductWeights } from "@/screens/CustomerOrders/utils"
import {
  calculateDeliveryDateFromShipDate,
  calculateLinkedQuantities,
  calculateShipDateFromDeliveryDate,
  toRoundingProduct,
} from "@/screens/CustomerOrders/utils"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import colors from "@/styles/colors"
import type { Option } from "@/types/form-fields"
import { RoundPrecisionUnit } from "@/types/rounding"
import { createUomsList } from "@/utils/conversion"
import { formatNumber } from "@/utils/number"
import { isBagsWholeNumber } from "@/utils/purchaseOrder"

interface CustomerPurchaseOrderDeliveryDateLineRowProps {
  lineItemIndex: number
  deliveryDateLineIndex: number
  shipToLocationId: string
  packageUsWeightUom: string | null
  packageMetricWeightUom: string | null
  packageConversion: ProductWeights
  isPalletsOverTotal: boolean
  isUnitsOverTotal: boolean
  isUomOverTotal: boolean
  lineTotals: CustomerPurchaseOrderPricingTotalsLineResult | undefined
  isCpoCancelled: boolean
  isPricingFetching: boolean
  onDelete: () => void
  onPriceOverrideSave: (updates: Partial<CpoDeliveryDateLineFormValues>) => void
}

const LINKED_QUANTITY_FIELDS: LinkedQuantityField[] = ["soPallets", "soUnits", "quantity"]

// Highlights only the input box — a background on the FormControl root would also paint the label
// and the space reserved under the field, so the yellow area looks shifted up. mui-theme.ts's
// MuiFormControl override paints both .MuiInputBase-root and .MuiInputBase-input white, so both need
// !important — recoloring only the root leaves the white input covering it.
const overTotalFieldSx = {
  "& .MuiInputBase-root, & .MuiInputBase-input": { backgroundColor: `${colors.tertiary.yellow["100"]} !important` },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: `${colors.tertiary.yellow["700"]} !important` },
}

export const CustomerPurchaseOrderDeliveryDateLineRow: FC<CustomerPurchaseOrderDeliveryDateLineRowProps> = ({
  lineItemIndex,
  deliveryDateLineIndex,
  shipToLocationId,
  packageUsWeightUom,
  packageMetricWeightUom,
  packageConversion,
  isPalletsOverTotal,
  isUnitsOverTotal,
  isUomOverTotal,
  lineTotals,
  isPricingFetching,
  isCpoCancelled,
  onDelete,
  onPriceOverrideSave,
}) => {
  const [isPriceOverrideOpen, setIsPriceOverrideOpen] = useState(false)

  const { isReadonly } = useSalesOrderPermissions()
  const { control, getValues, setValue } = useFormContext<CustomerPurchaseOrderLineItemsFormValues>()
  const { getTransitDays } = useGetShipFromTransitDays(shipToLocationId)

  const basePath = `lineItems.${lineItemIndex}.deliveryDateLines.${deliveryDateLineIndex}` as const

  const deliveryDateLineId = useWatch({ control, name: `${basePath}.deliveryDateLineId` })
  const soNumber = useWatch({ control, name: `${basePath}.soNumber` })
  const soStatus = useWatch({ control, name: `${basePath}.soStatus` })
  const isLineReadonly = isReadonly || isCpoCancelled || soStatus === SalesOrderStatus.Canceled
  const soPallets = useWatch({ control, name: `${basePath}.soPallets` })
  const quantity = useWatch({ control, name: `${basePath}.quantity` })
  const soUom = useWatch({ control, name: `${basePath}.soUom` })
  const { isVisible: isRoundOffVisible, evaluate: evaluateRoundOff, hide: hideRoundOff } = useRoundOffWarning()

  const { warehouses } = useLookupWarehouses()
  const shipFromOptions = useMemo<Option<string>[]>(
    () => warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouse.label })),
    [warehouses]
  )

  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))

  const uomOptions = useMemo<Option<string>[]>(
    () =>
      createUomsList(units, { packageUsWeightUom, packageMetricWeightUom }).map((uom) => ({
        value: uom.id,
        label: uom.name ?? uom.id,
      })),
    [units, packageUsWeightUom, packageMetricWeightUom]
  )

  const handlePriceOverrideSubmit = (formValues: { pricePerUom: number | null }) => {
    onPriceOverrideSave({ pricePerUom: formValues.pricePerUom, isManualPriceSet: true })
    setIsPriceOverrideOpen(false)
  }

  // Fills only the empty counterpart date from the transit time of the line's current ship-from;
  // a value the user already entered is never overwritten.
  const handleShipByDateChange = (value: string | null) => {
    if (!value || value === DateSpecialValue.ASAP || getValues(`${basePath}.deliverByDate`)) return

    const transitDays = getTransitDays(getValues(`${basePath}.shipFromLocationId`))
    if (transitDays === null) return

    setValue(`${basePath}.deliverByDate`, calculateDeliveryDateFromShipDate(value, transitDays), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleDeliverByDateChange = (value: string | null) => {
    // Any user-picked delivery date replaces the "today" that ASAP filled in.
    setValue(`${basePath}.isDeliverAsap`, false, { shouldDirty: true })

    if (!value || value === DateSpecialValue.ASAP || getValues(`${basePath}.shipByDate`)) return

    const transitDays = getTransitDays(getValues(`${basePath}.shipFromLocationId`))
    if (transitDays === null) return

    setValue(`${basePath}.shipByDate`, calculateShipDateFromDeliveryDate(value, transitDays), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  // A line always keeps a concrete delivery date (it decides which Sales Order the line belongs to),
  // so ASAP on either date resolves Deliver by to today and flags it, replacing any existing value.
  const handleAsapSelected = () => {
    setValue(`${basePath}.shipByDate`, DateSpecialValue.ASAP, { shouldDirty: true, shouldValidate: true })
    setValue(`${basePath}.deliverByDate`, dayjs().format(DATE_FORMAT.ISO_18601), {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue(`${basePath}.isDeliverAsap`, true, { shouldDirty: true })
  }

  const syncLinkedQuantities = (editedField: LinkedQuantityField, soUom: string | null) => {
    const next = calculateLinkedQuantities({
      editedField,
      values: {
        soPallets: getValues(`${basePath}.soPallets`),
        soUnits: getValues(`${basePath}.soUnits`),
        quantity: getValues(`${basePath}.quantity`),
      },
      soUom,
      conversion: packageConversion,
    })

    LINKED_QUANTITY_FIELDS.forEach((field) => {
      if (next[field] === getValues(`${basePath}.${field}`)) return
      setValue(`${basePath}.${field}`, next[field], { shouldDirty: true, shouldValidate: true })
    })
  }

  const handleLinkedQuantityChange = (editedField: LinkedQuantityField, source: "event" | "prop") => {
    if (source === "prop") return
    syncLinkedQuantities(editedField, getValues(`${basePath}.soUom`))
    evaluateRoundOff(getValues(`${basePath}.soPallets`))
  }

  const handleSoPalletsChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("soPallets", source)
  const handleSoUnitsChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("soUnits", source)
  const handleQuantityChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("quantity", source)

  // Quantity is the typed value, so a UoM change keeps it and recomputes units and pallets.
  const handleSoUomChange = (value: string | null) => {
    if (getValues(`${basePath}.quantity`) === null) return
    syncLinkedQuantities("quantity", value)
    evaluateRoundOff(getValues(`${basePath}.soPallets`))
  }

  const handleRoundOff = (result: RoundOffResult | null) => {
    hideRoundOff()
    if (!result) return

    if (result.precision === RoundPrecisionUnit.Bag) {
      setValue(`${basePath}.quantity`, result.quantity, { shouldDirty: true, shouldValidate: true })
      syncLinkedQuantities("quantity", getValues(`${basePath}.soUom`))
      return
    }

    setValue(`${basePath}.soPallets`, result.pallets, { shouldDirty: true, shouldValidate: true })
    syncLinkedQuantities("soPallets", getValues(`${basePath}.soUom`))
  }

  const showRoundOffWarning = isRoundOffVisible && !isLineReadonly && !!soUom && soPallets !== null && quantity !== null

  const price = lineTotals?.price
  const isManualPrice = price?.isManual ?? false
  const resolvedPricePerUnit = price?.pricePerUnit ?? null
  const resolvedPriceUomLabel = units.find((unit) => unit.id === price?.uomId)?.name ?? price?.uomId ?? null

  const convertedPrice = lineTotals?.converted
  const convertedPriceUomLabel =
    units.find((unit) => unit.id === convertedPrice?.pricePerUnitUomId)?.name ?? convertedPrice?.pricePerUnitUomId

  const total = lineTotals?.total.price ?? 0

  const hasPriceTier = !isManualPrice && !!lineTotals?.pricingEffectiveDate && resolvedPricePerUnit !== null

  const oppositeQuantity = lineTotals?.opposite?.quantity ?? null
  const oppositeUomLabel =
    units.find((unit) => unit.id === lineTotals?.opposite?.quantityUomId)?.name ?? lineTotals?.opposite?.quantityUomId

  return (
    <Box>
      <Box className='flex items-stretch gap-4 p-4 pb-8 align-top'>
        <Box className='flex w-20 flex-col gap-1'>
          <Typography variant='caption' className='text-gray-700'>
            Sales Order #
          </Typography>
          <Typography
            variant='subtitle2'
            className='text-primary'
            data-testid={`customer-orders.delivery-date-line-row.so-number-${deliveryDateLineId}`}
          >
            {soNumber ?? "—"}
          </Typography>
        </Box>

        <Box
          className='flex w-40 shrink-0 flex-col items-start gap-1'
          data-testid={`customer-orders.delivery-date-line-row.so-status-${deliveryDateLineId}`}
        >
          <Typography variant='caption' className='text-gray-700'>
            Status
          </Typography>
          {soStatus && <SalesOrderStatusChip status={soStatus} />}
        </Box>

        <FormSelect
          control={control}
          name={`${basePath}.shipFromLocationId`}
          label='Ship From'
          required
          hideErrorMessage
          placeholder='Select...'
          options={shipFromOptions}
          disabled={isLineReadonly}
          className='w-40'
          data-testid={`customer-orders.delivery-date-line-row.ship-from-select-${deliveryDateLineId}`}
        />

        <FormDatePickerWithCustomActions
          control={control}
          name={`${basePath}.shipByDate`}
          label='Ship by Date'
          required
          allowAsap
          onChange={handleShipByDateChange}
          onAsapSelect={handleAsapSelected}
          hideErrorMessage
          disabled={isLineReadonly}
          className='w-40'
          data-testid={`customer-orders.delivery-date-line-row.ship-by-date-field-${deliveryDateLineId}`}
        />

        <FormDatePickerWithCustomActions
          control={control}
          name={`${basePath}.deliverByDate`}
          label='Deliver by Date'
          required
          allowAsap
          onChange={handleDeliverByDateChange}
          onAsapSelect={handleAsapSelected}
          hideErrorMessage
          disabled={isLineReadonly}
          className='w-40'
          data-testid={`customer-orders.delivery-date-line-row.deliver-by-date-field-${deliveryDateLineId}`}
        />

        <FormNumericField
          control={control}
          name={`${basePath}.soPallets`}
          label='SO Pallets'
          required
          onChange={handleSoPalletsChange}
          hideErrorMessage
          disabled={isLineReadonly}
          className='w-28'
          sx={isPalletsOverTotal ? overTotalFieldSx : undefined}
          data-testid={`customer-orders.delivery-date-line-row.so-pallets-field-${deliveryDateLineId}`}
        />

        <FormNumericField
          control={control}
          name={`${basePath}.soUnits`}
          label='SO Units'
          required
          onChange={handleSoUnitsChange}
          hideErrorMessage
          disabled={isLineReadonly}
          className='w-28'
          sx={isUnitsOverTotal ? overTotalFieldSx : undefined}
          data-testid={`customer-orders.delivery-date-line-row.so-units-field-${deliveryDateLineId}`}
        />

        <Box className='flex w-28 flex-col gap-1'>
          <FormNumericField
            control={control}
            name={`${basePath}.quantity`}
            label='Quantity'
            required
            onChange={handleQuantityChange}
            hideErrorMessage
            disabled={isLineReadonly}
            data-testid={`customer-orders.delivery-date-line-row.quantity-field-${deliveryDateLineId}`}
          />
          {isPricingFetching ? (
            <Skeleton
              variant='text'
              className='w-16'
              data-testid={`customer-orders.delivery-date-line-row.opposite-quantity-skeleton-${deliveryDateLineId}`}
            />
          ) : (
            oppositeQuantity !== null && (
              <Typography variant='caption' className='text-gray-700'>
                ({formatNumber(oppositeQuantity, UOM_RENDER_PRECISION)} {oppositeUomLabel})
              </Typography>
            )
          )}
        </Box>

        <FormSelect
          control={control}
          name={`${basePath}.soUom`}
          label='SO UoM'
          required
          onChange={handleSoUomChange}
          hideErrorMessage
          placeholder='Select...'
          options={uomOptions}
          disabled={isLineReadonly}
          className='w-28'
          sx={isUomOverTotal ? overTotalFieldSx : undefined}
          data-testid={`customer-orders.delivery-date-line-row.so-uom-field-${deliveryDateLineId}`}
        />

        <Box className='flex flex-1 flex-col items-end gap-1'>
          <PriceUomDisplay
            price={resolvedPricePerUnit}
            priceUomLabel={resolvedPriceUomLabel}
            convertedPrice={convertedPrice?.pricePerUnit}
            convertedPriceUomLabel={convertedPriceUomLabel}
            total={total}
            isFetching={isPricingFetching}
            isManualPriceSet={isManualPrice}
            effectiveDate={hasPriceTier ? lineTotals?.pricingEffectiveDate : undefined}
            disabled={isLineReadonly}
            onEdit={() => setIsPriceOverrideOpen(true)}
            testIdSuffix={deliveryDateLineId}
          />
        </Box>

        {!isReadonly && (
          <IconButton
            onClick={onDelete}
            className='self-center'
            data-testid={`customer-orders.delivery-date-line-row.delete-button-${deliveryDateLineId}`}
          >
            <DeleteOutlined />
          </IconButton>
        )}

        {isPriceOverrideOpen && (
          <DeliveryDateLinePriceOverrideDialog
            pricePerUom={resolvedPricePerUnit}
            onClose={() => setIsPriceOverrideOpen(false)}
            onSubmit={handlePriceOverrideSubmit}
          />
        )}
      </Box>

      {showRoundOffWarning && (
        <Box className='pb-4'>
          <RoundOffWarning
            product={toRoundingProduct(packageConversion)}
            uomId={soUom!}
            pallets={soPallets!}
            quantity={quantity!}
            isPrecisionLocked={isBagsWholeNumber(toRoundingProduct(packageConversion), quantity!, soUom!)}
            onRound={handleRoundOff}
          />
        </Box>
      )}
    </Box>
  )
}
